import { useEffect, useRef } from "react";

const COOLDOWN_MS = 800; // one step per gesture, matching the card/circle transition

// While the section is on screen, each downward wheel / swipe / ↓ / PageDown
// shows the next item instead of scrolling the page. Once every item has been
// seen (autoplay counts too) the page scrolls on normally. Scrolling up is
// never blocked.
const useStepGate = (sectionRef, count, active, next) => {
  const seen = useRef(new Set());
  const lastStep = useRef(0);
  const nextRef = useRef(next);
  nextRef.current = next;

  useEffect(() => {
    seen.current.add(active);
  }, [active]);

  useEffect(() => {
    const engaged = () => {
      if (seen.current.size >= count || !sectionRef.current) return false;
      const r = sectionRef.current.getBoundingClientRect();
      const vh = window.innerHeight;
      const visible = Math.min(r.bottom, vh) - Math.max(r.top, 0);
      return visible >= 0.75 * Math.min(r.height, vh);
    };

    // First gesture eases the section to the centre of the screen (its outer
    // padding is what gets trimmed if it's taller); later gestures step.
    const step = () => {
      const now = Date.now();
      if (now - lastStep.current < COOLDOWN_MS) return;
      lastStep.current = now;
      const r = sectionRef.current.getBoundingClientRect();
      const offset = r.top - (window.innerHeight - r.height) / 2;
      if (Math.abs(offset) > 4) window.scrollBy({ top: offset, behavior: "smooth" });
      else nextRef.current();
    };

    const onWheel = (e) => {
      if (e.deltaY <= 0 || !engaged()) return;
      e.preventDefault();
      step();
    };

    let touchY = 0;
    const onTouchStart = (e) => {
      touchY = e.touches[0].clientY;
    };
    const onTouchMove = (e) => {
      const dy = touchY - e.touches[0].clientY;
      if (dy <= 0 || !engaged()) return;
      e.preventDefault();
      if (dy > 30) {
        step();
        touchY = e.touches[0].clientY;
      }
    };

    const onKey = (e) => {
      if ((e.key !== "ArrowDown" && e.key !== "PageDown") || !engaged()) return;
      e.preventDefault();
      step();
    };

    window.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("keydown", onKey);
    };
  }, [sectionRef, count]);
};

export default useStepGate;
